import type {
  WeeklyThemeDto,
  WeeklyThemeListEntry,
  WeeklyThemeUpsert,
} from "@care-by-ella/contracts";
import { API_BASE, apiFetch, tenantHeader, withCredentials } from "./api";
import { jsonOr401 } from "./clinician-api";

// TES-359 — veckotema authoring client, mirroring wellness-api.ts. Auth rides
// the httpOnly clinician session cookie, scoped by tenantHeader().

function commonHeaders(): Record<string, string> {
  return { "Content-Type": "application/json", ...tenantHeader() };
}

export class WeeklyThemeApiError extends Error {}

async function jsonOrError<T>(res: Response): Promise<T> {
  if (res.status === 401 || res.status === 403) {
    return jsonOr401<T>(res);
  }
  if (!res.ok) {
    let detail = `HTTP ${res.status}`;
    try {
      const body = (await res.json()) as { message?: unknown; error?: unknown };
      const msg = body.message ?? body.error;
      if (typeof msg === "string" && msg.trim()) detail = msg;
      else if (Array.isArray(msg) && msg.length) detail = msg.join("; ");
    } catch {
      // non-JSON body — keep the status-code detail
    }
    throw new WeeklyThemeApiError(detail);
  }
  return (await res.json()) as T;
}

const BASE = `${API_BASE}/employee-api/v1/weekly-themes`;

export async function listWeeklyThemes(): Promise<WeeklyThemeListEntry[]> {
  const res = await apiFetch(BASE, {
    headers: commonHeaders(),
    ...withCredentials,
  });
  return jsonOrError(res);
}

export async function upsertWeeklyTheme(
  phaseKey: string,
  body: WeeklyThemeUpsert,
): Promise<WeeklyThemeDto> {
  const res = await apiFetch(`${BASE}/${phaseKey}`, {
    method: "PUT",
    headers: commonHeaders(),
    body: JSON.stringify(body),
    ...withCredentials,
  });
  return jsonOrError(res);
}

export async function publishWeeklyTheme(
  phaseKey: string,
): Promise<WeeklyThemeDto> {
  const res = await apiFetch(`${BASE}/${phaseKey}/publish`, {
    method: "POST",
    headers: commonHeaders(),
    ...withCredentials,
  });
  return jsonOrError(res);
}

export async function unpublishWeeklyTheme(
  phaseKey: string,
): Promise<WeeklyThemeDto> {
  const res = await apiFetch(`${BASE}/${phaseKey}/unpublish`, {
    method: "POST",
    headers: commonHeaders(),
    ...withCredentials,
  });
  return jsonOrError(res);
}
